import fetchApi from '@/lib/tools/api'
import {
    CheckCircle as SuccessIcon,
    ContentCopy as CopyIcon,
    Error as ErrorIcon,
    PlayArrow as PlayIcon,
    Warning as WarningIcon
} from '@mui/icons-material'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material'
import React, {useCallback, useState} from 'react'
import {useNotify} from 'react-admin'
import {Script} from '../Scripts'

interface ScriptCardProps {
    script: Script
}

interface ScriptExecutionResponse {
    success: boolean
    output?: string
    message?: string
    error?: string
}

const ScriptCard: React.FC<ScriptCardProps> = ({ script }) => {
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [output, setOutput] = useState<string | null>(null)
    const [showOutput, setShowOutput] = useState(false)
    const [parameters, setParameters] = useState<Record<string, any>>({})
    const [confirmDialog, setConfirmDialog] = useState(false)
    
    const notify = useNotify()

    // Initialize parameters with default values
    React.useEffect(() => {
        const defaultParams: Record<string, any> = {}
        script.parameters.forEach(param => {
            if (param.defaultValue !== undefined) {
                defaultParams[param.name] = param.defaultValue
            } else if (param.type === 'boolean') {
                defaultParams[param.name] = false
            } else if (param.type === 'number') {
                defaultParams[param.name] = 0
            } else {
                defaultParams[param.name] = ''
            }
        })
        setParameters(defaultParams)
    }, [script.parameters])

    const handleParameterChange = useCallback((paramName: string, value: any) => {
        setParameters(prev => ({
            ...prev,
            [paramName]: value
        }))
    }, [])

    const executeScript = useCallback(async () => {
        setLoading(true)
        setError(null)
        setSuccess(false)
        setOutput(null)

        try {
            const response = await fetchApi<ScriptExecutionResponse>(
                '/scripts/execute',
                {
                    script_id: script.id,
                    parameters: parameters
                },
                null,
                'POST'
            )

            if (response.success) {
                setSuccess(true)
                setOutput(response.output || response.message || 'Script exécuté avec succès')
                setShowOutput(true)
                notify('Script exécuté avec succès', {type: 'success'})
                
                // Auto-reset success state after 10 seconds
                setTimeout(() => {
                    setSuccess(false)
                }, 10000)
            } else {
                throw new Error(response.error || response.message || 'Erreur inconnue lors de l\'exécution')
            }
        } catch (err: any) {
            const errorMessage = err.message || 'Erreur lors de l\'exécution du script'
            setError(errorMessage)
            setOutput(errorMessage)
            setShowOutput(true)
            notify(`Échec de l'exécution: ${errorMessage}`, {type: 'error'})
        } finally {
            setLoading(false)
        }
    }, [script.id, parameters, notify])

    const handleExecute = useCallback(() => {
        if (script.dangerous) {
            setConfirmDialog(true)
        } else {
            executeScript()
        }
    }, [script.dangerous, executeScript])

    const handleConfirmExecution = useCallback(() => {
        setConfirmDialog(false)
        executeScript()
    }, [executeScript])

    const copyToClipboard = useCallback(() => {
        if (output) {
            navigator.clipboard.writeText(output)
            notify('Sortie copiée dans le presse-papiers', {type: 'info'})
        }
    }, [output, notify])

    const renderParameterInput = (param: Script['parameters'][0]) => {
        const value = parameters[param.name]

        switch (param.type) {
            case 'boolean':
                return (
                    <FormControlLabel
                        key={param.name}
                        control={
                            <Checkbox
                                checked={value || false}
                                onChange={(e) => handleParameterChange(param.name, e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label={param.label}
                    />
                )

            case 'select':
                return (
                    <FormControl key={param.name} fullWidth margin="normal">
                        <InputLabel>{param.label}</InputLabel>
                        <Select
                            value={value || ''}
                            label={param.label}
                            onChange={(e) => handleParameterChange(param.name, e.target.value)}
                            disabled={loading}
                        >
                            {param.options?.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )

            case 'number':
                return (
                    <TextField
                        key={param.name}
                        fullWidth
                        margin="normal"
                        type="number"
                        label={param.label}
                        value={value || ''}
                        onChange={(e) => handleParameterChange(param.name, parseInt(e.target.value) || 0)}
                        disabled={loading}
                        helperText={param.description}
                        required={param.required}
                    />
                )

            case 'email':
                return (
                    <TextField
                        key={param.name}
                        fullWidth
                        margin="normal"
                        type="email"
                        label={param.label}
                        value={value || ''}
                        onChange={(e) => handleParameterChange(param.name, e.target.value)}
                        disabled={loading}
                        helperText={param.description}
                        required={param.required}
                    />
                )

            default: // text
                return (
                    <TextField
                        key={param.name}
                        fullWidth
                        margin="normal"
                        type="text"
                        label={param.label}
                        value={value || ''}
                        onChange={(e) => handleParameterChange(param.name, e.target.value)}
                        disabled={loading}
                        helperText={param.description}
                        required={param.required}
                    />
                )
        }
    }

    return (
        <>
            <Card 
                sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: script.dangerous ? '2px solid #f57c00' : undefined
                }}
            >
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center" gap={1}>
                            {script.dangerous && <WarningIcon color="warning" />}
                            <Typography variant="h6" component="div">
                                {script.name}
                            </Typography>
                        </Box>
                    }
                    subheader={script.description}
                />

                <CardContent sx={{ flexGrow: 1 }}>
                    {script.dangerous && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                                Script dangereux
                            </Typography>
                            <Typography variant="body2">
                                Ce script peut affecter les données. Une confirmation sera demandée.
                            </Typography>
                        </Alert>
                    )}

                    {script.longRunning && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Ce script peut prendre plusieurs minutes à s'exécuter.
                            </Typography>
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" icon={<SuccessIcon />} sx={{ mb: 2 }}>
                            Script exécuté avec succès !
                        </Alert>
                    )}

                    {error && !success && (
                        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">
                                    Exécution en cours...
                                    {script.longRunning && ' Cela peut prendre quelques instants.'}
                                </Typography>
                            </Box>
                        </Alert>
                    )}

                    {/* Parameters */}
                    {script.parameters.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Paramètres
                            </Typography>
                            {script.parameters.map(renderParameterInput)}
                        </Box>
                    )}

                    {/* Execute Button */}
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            color={script.dangerous ? "warning" : "primary"}
                            startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
                            onClick={handleExecute}
                            disabled={loading}
                            fullWidth
                        >
                            {loading ? 'Exécution...' : 'Exécuter'}
                        </Button>

                        {output && (
                            <Button
                                variant="outlined"
                                onClick={() => setShowOutput(!showOutput)}
                            >
                                {showOutput ? 'Masquer' : 'Voir'} sortie
                            </Button>
                        )}
                    </Box>

                    {/* Output */}
                    <Collapse in={showOutput}>
                        <Box sx={{ mt: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle2">
                                    Sortie du script
                                </Typography>
                                <IconButton onClick={copyToClipboard} size="small">
                                    <CopyIcon />
                                </IconButton>
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                value={output || ''}
                                variant="outlined"
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        backgroundColor: 'grey.50'
                                    }
                                }}
                            />
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon color="warning" />
                        Confirmation requise
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Vous êtes sur le point d'exécuter un script potentiellement dangereux :
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                        {script.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {script.description}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Êtes-vous sûr de vouloir continuer ?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(false)}>
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleConfirmExecution} 
                        color="warning" 
                        variant="contained"
                        startIcon={<WarningIcon />}
                    >
                        Confirmer et exécuter
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ScriptCard